import { ColumnDef, InitialTableState, AccessorFn } from '@tanstack/react-table';
import { isFunction, isString } from '../../../utils/guards';
import { reactNodeToString } from '../../../utils/reactNode';
import { UtilityColumns } from '../hooks/columnHooks';
import * as DataTableTypes from './DataTableTypes';
import { CELL_MIN_WIDTH, CELL_MIN_WIDTH_WITH_COLUMN_SIZING } from './getStyling';

export const columnToReactTableModel = <
  D extends object = {},
  DataColumn extends object = D,
  SortModel extends string = ''
>(
  columns: DataTableTypes.Column<DataColumn, SortModel>[],
  resizeTableColumns: boolean
): ColumnDef<DataColumn, SortModel>[] =>
  columns.map((column) => {
    const {
      CellAfterContent,
      CellBeforeContent,
      cellDataTest,
      defaultWidth,
      columnName,
      blockColumnHiding,
      ellipsisOnOverflow,
      Header,
      enableSorting,
      id,
      align,
      minWidth,
      maxWidth,
      width,
      sticky
    } = column;
    const columnDef: Partial<ColumnDef<DataColumn, SortModel>> = {
      header: Header,
      enableSorting,
      id,
      minSize: minWidth ?? (resizeTableColumns ? CELL_MIN_WIDTH_WITH_COLUMN_SIZING : CELL_MIN_WIDTH),
      maxSize: maxWidth,
      size: width ?? defaultWidth,
      meta: {
        columnName:
          (columnName ?? typeof Header === 'string') ? (Header as string) : reactNodeToString(Header?.({} as any)),
        sticky,
        blockColumnHiding,
        ellipsisOnOverflow,
        defaultWidth,
        align,
        CellAfterContent: CellAfterContent as unknown as DataTableTypes.CellBeforeAfterContent<{}> | undefined,
        CellBeforeContent: CellBeforeContent as unknown as DataTableTypes.CellBeforeAfterContent<{}> | undefined,
        cellDataTest,
        forceRender: hasAccessorFn(column.accessor)
      },
      ...(hasAccessorFn(column.accessor) && {
        cell: (row) => (column.accessor as (value: DataColumn) => string | number | JSX.Element)(row.row.original)
      }),
      ...(hasAccessorString(column.accessor) && {
        accessorKey: column.accessor
      }),
      ...(hasAccessorString(column.accessor) &&
        hasCellFunction(column) && {
          cell: (row) =>
            column.Cell({
              value: row.getValue<DataColumn>(),
              row: { original: row.row.original }
            })
        })
    };
    return columnDef as unknown as ColumnDef<DataColumn, SortModel>;
  });

const hasAccessorString = <D extends object = {}, DataColumn extends object = D>(
  accessor: string | number | symbol | ((value: DataColumn) => string | number | JSX.Element)
): accessor is string => isString(accessor);

const hasAccessorFn = <D extends object = {}, DataColumn extends object = D, SortModel extends string = ''>(
  accessor: unknown
): accessor is AccessorFn<DataColumn, SortModel> => isFunction(accessor);

const hasCellFunction = <D extends object = {}, DataColumn extends object = D, SortModel extends string = ''>(
  column: DataTableTypes.Column<DataColumn, SortModel>
): column is DataTableTypes.Column<DataColumn, SortModel> & {
  // biome-ignore lint/correctness/noEmptyPattern: ?
  Cell: ({}) => void;
} =>
  // Cell value is conditional, so if isFunction is true, Cell function exists
  isFunction((column as any).Cell);

export const initialStateToReactTableModel = (
  initialState: DataTableTypes.InitialState,
  resizeTableColumns: boolean
): InitialTableState => {
  const { columnOrder, hiddenColumns, columnSizing } = initialState;

  return {
    columnSizing: resizeTableColumns ? (columnSizing ?? {}) : {},
    columnOrder: moveUtilityColumnsToBeginning(columnOrder),
    columnVisibility: hiddenColumns?.reduce((acc, column) => ({ ...acc, [column]: false }), {})
  };
};

export const moveUtilityColumnsToBeginning = (columnOrder: DataTableTypes.InitialState['columnOrder']) => {
  const filteredArrayOfColumns =
    columnOrder?.filter((column) => column !== UtilityColumns.EXPANDER && column !== UtilityColumns.SELECTION) ?? [];

  return [UtilityColumns.SELECTION, UtilityColumns.EXPANDER, ...filteredArrayOfColumns];
};
